import type { Href } from 'expo-router';

import { ROUTES } from '@/constants/routes';

export type BackAction = { kind: 'history' } | { kind: 'replace'; href: Href };

export type BackContext = {
  /** Whether the entry before this one belongs to the app, so going back stays inside it. */
  hasAppEntryBehind: boolean;
  /** The screen's declared parent, used when there is nothing of ours to go back to. */
  backRoute?: Href;
};

/**
 * Decides what Back does, for the header control, Android hardware back and every place the app
 * goes back on its own.
 *
 * Back follows history whenever the previous entry is ours, so on web it lands exactly where the
 * browser's own back button would. With nothing of ours behind (a deep link, a new tab, a reload
 * on native) it replaces the page with its parent rather than pushing one: a push would leave the
 * page just left one browser-back away, and the two back buttons would send the reader in a loop.
 * The Directory is the parent of last resort on every platform, and Back never leaves the app.
 */
export function resolveBack({ hasAppEntryBehind, backRoute }: BackContext): BackAction {
  if (hasAppEntryBehind) {
    return { kind: 'history' };
  }

  return { kind: 'replace', href: backRoute ?? (ROUTES.DIRECTORY as Href) };
}
